import React, { memo, useCallback } from 'react';
import styled, { withTheme } from 'styled-components';
import { Box, IconButton, Tooltip } from '@mic3/platform-ui';

import Icon from 'app/components/atoms/Icon/Icon';
import { MARKDOWN_TOKENS, wrapInputSelection, restoreInputCursor } from 'app/utils/markdown/markdownUtils';

const Bar = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding-bottom: 8px;
    & .MuiIconButton-root {
        padding: 4px;
    }
`;

const FORMAT_ACTIONS = [
    { name: 'format-bold', title: 'bold', token: MARKDOWN_TOKENS.bold },
    { name: 'format-italic', title: 'Italic', token: MARKDOWN_TOKENS.italic },
    { name: 'format-strikethrough', title: 'strike', token: MARKDOWN_TOKENS.strikethrough },
    { name: 'xml', title: 'inline code', token: MARKDOWN_TOKENS.code },
    { name: 'keyboard-return', title: 'multi line', token: MARKDOWN_TOKENS.codeBlock },
];

const FormatButton = memo(({ action, onApply, iconColor }) => {
    const onClick = useCallback(() => onApply(action.token), [action.token, onApply]);
    return (
        <Tooltip title={action.title} placement="top">
            <IconButton onClick={onClick}>
                <Icon name={action.name} size="sm" hexColor={iconColor} />
            </IconButton>
        </Tooltip>
    );
});

const FormatToolbar = ({ inputRef, value, onChange, theme }) => {
    const isLight =
        typeof theme?.isLightColor === 'function' ? theme.isLightColor(theme?.material?.colors?.background?.default) : false;
    const iconColor = isLight ? theme.material.colors.text.label : theme.material.colors.disabled.color;
    const applyFormat = useCallback(
        token => {
            const input = inputRef && inputRef.current;
            const next = wrapInputSelection(input, value, token);
            onChange({ target: { value: next.value } });
            restoreInputCursor(input, next.selectionStart, next.selectionEnd);
        },
        [inputRef, value, onChange]
    );

    return (
        <Bar>
            {FORMAT_ACTIONS.map(action => (
                <FormatButton key={action.name} action={action} onApply={applyFormat} iconColor={iconColor} />
            ))}
        </Bar>
    );
};

export default memo(withTheme(FormatToolbar));
